import React, { useState } from 'react';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';
import './Expenses.css';
import { useAuth } from '../../context/AuthContext';

const EMPTY = {
  expense_date: today(),
  mode: 'Cash',
  reference_no: '',
  file: null,
  items: [{ category_id: '', payee: '', amount: '', description: '' }],
};

export default function Expenses() {
  const { can, canAccess } = useAuth();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState(today());
  const [catFilter, setCatFilter] = useState('');

  React.useEffect(() => {
    const d = new Date();
    setFrom(new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10));
  }, []);

  const { data, loading, error, reload } = useApi(
    () => from && to ? api.expenses.list(from, to, catFilter) : Promise.resolve({ rows: [], total: 0, by_category: {} }),
    [from, to, catFilter],
    { rows: [], total: 0, by_category: {}, count: 0 }
  );

  const { data: categories, loading: catsLoading, error: catError, reload: reloadCats } = useApi(() => api.expenses.categories(), [], []);
  const { data: banksData } = useApi(() => api.banks.list(), [], { banks: [] });

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [msg, setMsg] = useState('');
  const [newCat, setNewCat] = useState('');
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };
  const addItem = () => setForm({ ...form, items: [...form.items, { category_id: '', payee: '', amount: '', description: '' }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const updateItem = (idx, f, v) => {
    const it = [...form.items];
    it[idx][f] = v;
    setForm({ ...form, items: it });
  };
  const handleViewAttachment = async (id) => {
    try {
      const blob = await api.expenses.downloadAttachment(id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) {
      notify('❌ Error: ' + e.message);
    }
  };

  const handleSave = async () => {
    const validItems = form.items.filter(it => it.category_id && Number(it.amount) > 0);
    if (validItems.length === 0) { notify('❌ Add at least one valid expense item'); return; }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('expense_date', form.expense_date);
      fd.append('mode', form.mode);
      fd.append('reference_no', form.reference_no);
      if (form.file) fd.append('file', form.file);
      fd.append('items', JSON.stringify(validItems));

      if (editId) {
        // Edit is still single row in this version for simplicity, but backend handles multi-save for new
        await api.expenses.update(editId, { ...validItems[0], expense_date: form.expense_date, mode: form.mode, reference_no: form.reference_no });
        notify('✅ Expense updated');
      } else {
        await api.expenses.create(fd);
        notify('✅ Expenses recorded');
      }

      setShowForm(false);
      setForm(EMPTY);
      setEditId(null);
      reload();
    } catch (e) {
      notify('❌ ' + e.message);
    }
    setSaving(false);
  };

  const handleAddCategory = async () => {
    const n = newCat.trim();
    if (!n) return;
    try {
      await api.expenses.addCategory(n);
      notify('✅ Category added');
      setNewCat('');
      reloadCats();
    } catch (e) {
      notify('❌ ' + e.message);
    }
  };

  const handleDelete = async () => {
    try {
      await api.expenses.remove(confirm.id);
      notify('✅ Expense deleted (ledger reversed)');
      reload();
    } catch (e) {
      notify('❌ ' + e.message);
    }
    setConfirm(null);
  };

  const openEdit = (r) => {
    setEditId(r.id);
    setForm({
      expense_date: r.expense_date,
      mode: r.mode,
      reference_no: r.reference_no,
      file: null,
      items: [{
        category_id: r.category_id,
        payee: r.payee,
        amount: r.amount,
        description: r.description
      }]
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const banks = banksData.banks || [];
  if (!canAccess('expenses')) {
    return (
      <div className="expenses-page">
        <ErrBanner msg="Access Denied: You do not have permission to access expenses." />
      </div>
    );
  }
  return (
    <div className="expenses-page">
      <div className="page-header">
        <h2 className="page-title">Expenses</h2>
        {can('expenses', 'create') && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              if (showForm) { setShowForm(false); setEditId(null); setForm(EMPTY); }
              else { setShowForm(true); setEditId(null); setForm(EMPTY); }
            }}
          >
            {showForm ? '✕ Cancel' : '＋ New Expense'}
          </button>
        )}
      </div>

      {msg && <div className="exp-toast">{msg}</div>}
      {error && <ErrBanner msg={error} />}
      {catError && <ErrBanner msg={`Categories: ${catError}`} />}

      {showForm && (
        <div className="section-card">
          <div className="section-header">{editId ? `✏️ Edit Expenses — ${fmtDate(form.expense_date)}` : 'Record New Expense'}</div>
          <div style={{ padding: '0 16px 16px' }}>
            <div className="exp-form-grid" style={{ marginBottom: 16 }}>
              <div className="form-group">
                <label>Date *</label>
                <input type="date" value={form.expense_date} onChange={e => setForm({ ...form, expense_date: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Mode *</label>
                <select value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })}>
                  <option value="Cash">Cash</option>
                  {banks.map(b => <option key={b.id} value={`Bank:${b.id}`}>{b.name}</option>)}
                  <option value="UPI">UPI</option>
                </select>
              </div>
              <div className="form-group">
                <label>Ref No / Bill No</label>
                <input value={form.reference_no} onChange={e => setForm({ ...form, reference_no: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Attachment</label>
                <div className="custom-file-upload">
                  {form.file ? `📄 ${form.file.name}` : '📁 Choose File'}
                  <input type="file" onChange={e => setForm({ ...form, file: e.target.files[0] })} />
                </div>
              </div>
            </div>

            <table className="data-table" style={{ marginBottom: 16 }}>
              <thead>
                <tr>
                  <th style={{ width: '25%' }}>Category</th>
                  <th style={{ width: '20%' }}>Payee</th>
                  <th style={{ width: '15%' }}>Amount</th>
                  <th>Description</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, idx) => (
                  <tr key={idx}>
                    <td>
                      <select className="form-control" value={it.category_id} onChange={e => updateItem(idx, 'category_id', e.target.value)}>
                        <option value="">-- Select --</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </td>
                    <td><input className="form-control" value={it.payee} onChange={e => updateItem(idx, 'payee', e.target.value)} /></td>
                    <td><input type="number" className="form-control" value={it.amount} onChange={e => updateItem(idx, 'amount', e.target.value)} /></td>
                    <td><input className="form-control" value={it.description} onChange={e => updateItem(idx, 'description', e.target.value)} /></td>
                    <td>
                      {form.items.length > 1 && (
                        <button className="btn btn-sm" style={{ color: 'var(--danger)' }} onClick={() => removeItem(idx)}>🗑️</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button className="btn btn-secondary btn-sm" onClick={addItem}>＋ Add Another Line</button>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="New category" style={{ width: 120 }} className="form-control" />
                  <button className="btn btn-sm" onClick={handleAddCategory}>+ Add</button>
                </div>
                {((editId && can('expenses', 'update')) || (!editId && can('expenses', 'create'))) && (
                  <button className="btn btn-primary" disabled={saving} onClick={handleSave}>
                    {saving ? '⏳ Saving...' : editId ? 'Update' : 'Save Expense'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="exp-filters">
        <div className="form-group">
          <label>From</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="form-group">
          <label>To</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Category</label>
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)}>
            <option value="">All</option>
            {(categories || []).map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div style={{ marginLeft: 'auto', alignSelf: 'center' }}>
          <strong>Total: </strong>
          <span style={{ fontWeight: 700, fontFamily: 'monospace', color: 'var(--danger)' }}>
            ₹ {fmtNum(data.total)}
          </span>
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}> · {data.count} entries</span>
        </div>
      </div>

      {/* Category breakdown */}
      {Object.keys(data.by_category || {}).length > 0 && (
        <div className="section-card">
          <div className="section-header">By Category</div>
          <div className="exp-category-grid">
            {Object.entries(data.by_category).map(([name, total]) => (
              <div key={name} className="exp-cat-tile">
                <div className="exp-cat-name">{name}</div>
                <div className="exp-cat-amount">
                  ₹ {fmtNum(total)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? <Spinner /> : (
        <div className="section-card" style={{ overflowX: 'auto' }}>
          <div className="section-header">Expense Entries</div>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Date</th>
                <th style={{ textAlign: 'left' }}>Voucher</th>
                <th style={{ textAlign: 'left' }}>Category</th>
                <th style={{ textAlign: 'left' }}>Payee</th>
                <th style={{ textAlign: 'left' }}>Description</th>
                <th style={{ textAlign: 'left' }}>Mode</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 ? (
                <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                  No expenses match your filters
                </td></tr>
              ) : data.rows.map(r => (
                <tr key={r.id}>
                  <td style={{ textAlign: 'left' }}>{fmtDate(r.expense_date)}</td>
                  <td style={{ textAlign: 'left', fontWeight: 600, fontFamily: 'monospace' }}>
                    {r.voucher_no}
                    {r.file_path && (
                      <button onClick={() => handleViewAttachment(r.id)} title="View Attachment" style={{ marginLeft: 6, background: 'none', border: 'none', cursor: 'pointer' }}>📎</button>
                    )}
                  </td>
                  <td style={{ textAlign: 'left' }}>{r.category_name_live || r.category_name}</td>
                  <td style={{ textAlign: 'left' }}>{r.payee || '—'}</td>
                  <td style={{ textAlign: 'left', fontSize: 11.5, color: 'var(--text-secondary)' }}>{r.description || '—'}</td>
                  <td style={{ textAlign: 'left' }}>{r.mode}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--danger)' }}>
                    ₹ {fmtNum(r.amount)}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      {can('expenses', 'update') && (
                        <button className="btn btn-sm" onClick={() => openEdit(r)}>✏️</button>
                      )}
                      {can('expenses', 'delete') && (
                        <button className="btn btn-sm" style={{ color: 'var(--danger)' }}
                          onClick={() => setConfirm(r)}>🗑</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title="Delete Expense?"
        message={confirm ? `Delete expense ${confirm.voucher_no}? The ledger entry will be reversed.` : ''}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
        confirmLabel="Delete"
      />
    </div>
  );
}
